import { ChangeDetectionStrategy, Component, ElementRef, EventEmitter, Injector, Input, Output, TemplateRef, ViewChild, afterNextRender, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { AgendaMonth } from './agenda.model';

@Component({
  selector: 'app-agenda',
  standalone: true,
  imports: [CommonModule, MatButtonModule, MatIconModule, MatProgressSpinnerModule],
  templateUrl: './agenda.component.html',
  styleUrl: './agenda.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AgendaComponent<T> {
  @Input({ required: true }) title = '';
  @Input({ required: true }) months: AgendaMonth<T>[] = [];
  @Input({ required: true }) itemTemplate!: TemplateRef<{ $implicit: T; isPast?: boolean }>;
  @Input() loading = false;
  @Input() error = false;
  @Input() canView = true;
  @Input() emptyMessage = 'Sin actividades registradas este mes.';
  @Input() preferUpcoming = true;
  @Input() upcomingEmptyMessage = 'No hay actividades próximas en este período.';
  @Output() retry = new EventEmitter<void>();
  readonly today = new Date();
  readonly currentYear = this.today.getFullYear();
  private readonly injector = inject(Injector);
  private list?: ElementRef<HTMLElement>;

  get hasUpcomingItems(): boolean {
    return this.months.some(month => month.days.some(day => !day.isPast && day.items.length > 0));
  }

  hasUpcomingDays(month: AgendaMonth<T>): boolean {
    return month.days.some(day => !day.isPast);
  }

  @ViewChild('agendaList') private set agendaList(value: ElementRef<HTMLElement> | undefined) {
    this.list = value;
    if (value) afterNextRender(() => this.goToToday('instant'), { injector: this.injector });
  }

  goToToday(behavior: ScrollBehavior = 'smooth'): void {
    const list = this.list?.nativeElement;
    const target = (this.preferUpcoming ? list?.querySelector<HTMLElement>('[data-today-marker]') : null) ??
      list?.querySelector<HTMLElement>('[data-today="true"]') ??
      (this.preferUpcoming ? list?.querySelector<HTMLElement>('[data-past="false"], [data-upcoming-empty]') : null) ??
      list?.querySelector<HTMLElement>('[data-current-month="true"]');
    target?.scrollIntoView({ behavior, block: 'start' });
  }
}
